import React, { useState, useMemo, useCallback, memo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    LinearProgress,
    Divider,
    IconButton,
    useTheme,
    alpha,
    Tooltip,
    Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { DeviceInfo, OperatorCounts } from './types';
import { getOperatorColor } from './utils';

interface DeviceDashboardProps {
    processedDevices: DeviceInfo[];
    operatorCounts: OperatorCounts;
    totalDevices: number;
    recentDevices: number;
    outdatedDevices: number;
    devicesWithoutTimestamp: number;
    theme: any;
    sourceType: 'all' | 'inventory' | 'service';
    onSourceChange: (source: 'all' | 'inventory' | 'service') => void;
}

// Używamy memo do optymalizacji re-renderów
const DeviceDashboard: React.FC<DeviceDashboardProps> = memo(({
    processedDevices,
    operatorCounts,
    totalDevices,
    recentDevices,
    outdatedDevices,
    devicesWithoutTimestamp,
    theme,
    sourceType = 'all',
    onSourceChange
}) => {
    const [expanded, setExpanded] = useState(false);
    
    // Używamy useCallback, aby owinąć funkcję zmiany źródła danych
    const handleSourceChange = useCallback((source: 'all' | 'inventory' | 'service') => {
        // Dodajemy kontrolę, aby uniknąć niepotrzebnych wywołań gdy źródło się nie zmienia
        if (source !== sourceType) {
            onSourceChange(source);
        }
    }, [onSourceChange, sourceType]);
    
    // Dodajemy useCallback dla funkcji rozwijania/zwijania dashboardu
    const toggleExpanded = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);
    
    // Colors based on theme
    const headerBackground = theme.name === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)';
    const sectionBackground = theme.name === 'dark' ? 'transparent' : 'white';
    const dividerColor = theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
    
    // Calculate pie chart data
    const pieChartData = useMemo(() => [
        { name: 'Orange', value: operatorCounts.Orange, color: getOperatorColor('Orange') },
        { name: 'Play', value: operatorCounts.Play, color: getOperatorColor('Play') },
        { name: 'Plus', value: operatorCounts.Plus, color: getOperatorColor('Plus') },
        { name: 'Unknown', value: operatorCounts.Unknown, color: getOperatorColor('Unknown') }
    ], [operatorCounts]);

    // Find most common operator
    const mostCommonOperator = useMemo(() => {
        return pieChartData.reduce((prev, current) => 
            (prev.value > current.value) ? prev : current, 
            { name: 'None', value: 0, color: '#ccc' }
        );
    }, [pieChartData]);
    
    // Calculate label statistics
    const labeledDevices = useMemo(() => 
        processedDevices.filter(d => d.label && Array.isArray(d.label) && d.label.length > 0).length, 
    [processedDevices]);
    
    // Create label counts
    const labelCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        processedDevices.forEach(device => {
            if (device.label && Array.isArray(device.label)) {
                device.label.forEach(labelObj => {
                    if (labelObj && labelObj.label) {
                        const labelText = labelObj.label.trim();
                        counts[labelText] = (counts[labelText] || 0) + 1;
                    }
                });
            }
        });
        return counts;
    }, [processedDevices]);
    
    // Sort labels by count
    const sortedLabels = useMemo(() => 
        Object.entries(labelCounts).sort((a, b) => b[1] - a[1]), 
    [labelCounts]);
    
    // Find most common label
    const mostCommonLabel = useMemo(() => {
        if (sortedLabels.length === 0) return { name: 'None', count: 0 };
        return { name: sortedLabels[0][0], count: sortedLabels[0][1] };
    }, [sortedLabels]);

    return (
        <Paper 
            sx={{ 
                mb: 1, 
                borderRadius: 1, 
                overflow: 'hidden',
                border: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
                boxShadow: theme.name === 'dark' ? '0 4px 23px 0 rgba(0, 0, 0, 0.2)' : '0 2px 10px 0 rgba(0, 0, 0, 0.05)',
            }}
        >
            {/* Dashboard Header with Toggle */}
            <Box 
                sx={{ 
                    p: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    bgcolor: headerBackground,
                    border: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            width: 200, // Fixed width container for title
                            flexShrink: 0 // Prevent shrinking
                        }}
                        onClick={toggleExpanded}
                    >
                        <SignalCellularAltIcon sx={{ mr: 1, color: theme.name === 'dark' ? '#64B5F6' : '#1976D2' }} fontSize="small" />
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: theme.name === 'dark' ? theme.palette.text.primary : theme.palette.text.primary
                            }}
                        >
                            {sourceType === 'service' ? 'Service Devices' : 
                             sourceType === 'inventory' ? 'Inventory Devices' : 
                             'All Devices'}
                        </Typography>
                    </Box>

                    {/* Source selector buttons */}
                    <Box sx={{ 
                        display: 'flex', 
                        ml: 3,
                        backgroundColor: alpha(theme.palette.background.paper, 0.25),
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                    }}>
                        <Button 
                            size="small"
                            sx={{ 
                                minWidth: 0, 
                                px: 1.5,
                                backgroundColor: sourceType === 'all' ? 
                                    alpha(theme.palette.primary.main, 0.2) : 'transparent',
                                color: sourceType === 'all' ? 
                                    theme.palette.primary.main : theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: sourceType === 'all' ? 
                                        alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.action.hover, 0.1)
                                }
                            }}
                            onClick={() => handleSourceChange('all')}
                        >
                            All
                        </Button>
                        
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.2 }} />
                        
                        <Button 
                            size="small"
                            sx={{ 
                                minWidth: 0, 
                                px: 1.5,
                                backgroundColor: sourceType === 'inventory' ? 
                                    alpha(theme.palette.primary.main, 0.2) : 'transparent',
                                color: sourceType === 'inventory' ? 
                                    theme.palette.primary.main : theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: sourceType === 'inventory' ? 
                                        alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.action.hover, 0.1)
                                }
                            }}
                            onClick={() => handleSourceChange('inventory')}
                        >
                            Inventory
                        </Button>
                        
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.2 }} />
                        
                        <Button 
                            size="small"
                            sx={{ 
                                minWidth: 0, 
                                px: 1.5,
                                backgroundColor: sourceType === 'service' ? 
                                    alpha(theme.palette.primary.main, 0.2) : 'transparent',
                                color: sourceType === 'service' ? 
                                    theme.palette.primary.main : theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: sourceType === 'service' ? 
                                        alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.action.hover, 0.1)
                                }
                            }}
                            onClick={() => handleSourceChange('service')}
                        >
                            Service
                        </Button>
                    </Box>
                </Box>

                <IconButton size="small" onClick={toggleExpanded}>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>
            
            {/* Main Dashboard Content - Always Visible */}
            <Box sx={{ p: 2, bgcolor: sectionBackground }}>
                <Grid container spacing={2}>
                    {/* Left column: Stats summary */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={1.5}>
                                <Grid item xs={6} sm={3}>
                                    <Box 
                                        sx={{ 
                                            textAlign: 'center', 
                                            p: 1, 
                                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                                            borderRadius: 1,
                                            bgcolor: theme.name === 'dark' 
                                                ? alpha(theme.palette.primary.main, 0.15) 
                                                : alpha(theme.palette.primary.main, 0.05),
                                            boxShadow: theme.name === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight="bold" color="primary">
                                            {totalDevices}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Total
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box 
                                        sx={{ 
                                            textAlign: 'center', 
                                            p: 1, 
                                            borderLeft: `3px solid ${theme.palette.success.main}`,
                                            borderRadius: 1,
                                            bgcolor: theme.name === 'dark' 
                                                ? alpha(theme.palette.success.main, 0.15) 
                                                : alpha(theme.palette.success.main, 0.05),
                                            boxShadow: theme.name === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight="bold" color="success.main">
                                            {recentDevices}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Current
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box 
                                        sx={{ 
                                            textAlign: 'center', 
                                            p: 1, 
                                            borderLeft: `3px solid ${theme.palette.warning.main}`,
                                            borderRadius: 1,
                                            bgcolor: theme.name === 'dark' 
                                                ? alpha(theme.palette.warning.main, 0.15) 
                                                : alpha(theme.palette.warning.main, 0.05),
                                            boxShadow: theme.name === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                                            {outdatedDevices}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Outdated
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box 
                                        sx={{ 
                                            textAlign: 'center', 
                                            p: 1, 
                                            borderLeft: `3px solid ${theme.name === 'dark' ? '#666666' : theme.palette.text.disabled}`,
                                            borderRadius: 1,
                                            bgcolor: theme.name === 'dark' 
                                                ? alpha(theme.palette.text.disabled, 0.15) 
                                                : alpha(theme.palette.text.disabled, 0.05),
                                            boxShadow: theme.name === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight="bold" color="text.secondary">
                                            {devicesWithoutTimestamp}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            No Data
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Modem Distribution
                            </Typography>
                            <Box 
                                sx={{ 
                                    mb: 2,
                                    p: 1.5,
                                    bgcolor: theme.name === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.default, 0.5),
                                    borderRadius: 1,
                                    boxShadow: theme.name === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none',
                                }}
                            >
                                {(() => {
                                    // Prepare modem data
                                    const modemCounts: Record<string, number> = {};
                                    let noModemCount = 0;
                                    
                                    processedDevices.forEach(device => {
                                        if (device.modem && device.modem.trim() !== '') {
                                            const cleanModem = device.modem.replace(/\r|\n/g, '').trim();
                                            modemCounts[cleanModem] = (modemCounts[cleanModem] || 0) + 1;
                                        } else {
                                            noModemCount++;
                                        }
                                    });
                                    
                                    // Sort by count (descending)
                                    const sortedModems = Object.entries(modemCounts)
                                        .sort(([, countA], [, countB]) => countB - countA)
                                        .slice(0, 4); // Top 4 modems
                                    
                                    // Assign colors for different modem types
                                    const modemColors: Record<string, string> = {};
                                    
                                    // Używam specjalnych kolorów dla konkretnych modeli modemów
                                    // Odcienie czerwieni dla rodziny EM
                                    if (Object.keys(modemCounts).some(m => m.includes('EM'))) {
                                        Object.keys(modemCounts).forEach(modem => {
                                            if (modem.includes('EM7455B')) {
                                                modemColors[modem] = '#d32f2f'; // Ciemniejsza czerwień
                                            } else if (modem.includes('EM7455')) {
                                                modemColors[modem] = '#f44336'; // Czerwień
                                            } else if (modem.includes('EM')) {
                                                modemColors[modem] = '#ef5350'; // Jaśniejsza czerwień
                                            } else if (modem.includes('ME906E')) {
                                                modemColors[modem] = '#1976d2'; // Ciemniejszy niebieski
                                            } else if (modem.includes('ME906s-158')) {
                                                modemColors[modem] = '#2196f3'; // Niebieski
                                            } else if (modem.includes('ME')) {
                                                modemColors[modem] = '#64b5f6'; // Jaśniejszy niebieski
                                            } else {
                                                modemColors[modem] = theme.palette.secondary.main; // Dla innych modemów
                                            }
                                        });
                                    } else {
                                        // Fallback do oryginalnych kolorów jeśli nie ma modemów EM/ME
                                        const colors = [
                                            theme.palette.info.main,
                                            theme.palette.info.light,
                                            theme.palette.secondary.main,
                                            theme.palette.secondary.light
                                        ];
                                        
                                        sortedModems.forEach(([modem], index) => {
                                            modemColors[modem] = colors[index % colors.length];
                                        });
                                    }
                                    
                                    // Format display name to make it more readable
                                    const formatModemName = (name: string): string => {
                                        if (name.length > 10) {
                                            return name.substring(0, 10) + '...';
                                        }
                                        return name;
                                    };
                                    
                                    // Find most common modem
                                    const mostCommonModem = sortedModems.length > 0
                                        ? { name: sortedModems[0][0], count: sortedModems[0][1] }
                                        : { name: 'None', count: 0 };
                                    
                                    // Render progress bars
                                    return (
                                        <>
                                            {sortedModems.map(([modem, count]) => (
                                                <Box key={modem} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    <Tooltip title={modem} arrow placement="left">
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                width: 70,
                                                                color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {formatModemName(modem)}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                                                        <LinearProgress 
                                                            variant="determinate" 
                                                            value={(count / totalDevices) * 100} 
                                                            sx={{ 
                                                                height: 8, 
                                                                backgroundColor: theme.name === 'dark' 
                                                                    ? alpha(modemColors[modem], 0.3)
                                                                    : alpha(modemColors[modem], 0.2),
                                                                '& .MuiLinearProgress-bar': {
                                                                    backgroundColor: modemColors[modem]
                                                                },
                                                                borderRadius: 4
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography 
                                                        variant="caption"
                                                        sx={{ 
                                                            color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit',
                                                            fontWeight: 'medium'
                                                        }}
                                                    >
                                                        {count}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            
                                            {noModemCount > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            width: 70,
                                                            color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit'
                                                        }}
                                                    >
                                                        Unknown
                                                    </Typography>
                                                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                                                        <LinearProgress 
                                                            variant="determinate" 
                                                            value={(noModemCount / totalDevices) * 100} 
                                                            sx={{ 
                                                                height: 8, 
                                                                backgroundColor: theme.name === 'dark' 
                                                                    ? alpha(theme.palette.text.disabled, 0.3)
                                                                    : alpha(theme.palette.text.disabled, 0.2),
                                                                '& .MuiLinearProgress-bar': {
                                                                    backgroundColor: theme.name === 'dark' 
                                                                        ? alpha('#666666', 0.8) 
                                                                        : alpha(theme.palette.text.disabled, 0.7)
                                                                },
                                                                borderRadius: 4
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography 
                                                        variant="caption"
                                                        sx={{ 
                                                            color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit',
                                                            fontWeight: 'medium'
                                                        }}
                                                    >
                                                        {noModemCount}
                                                    </Typography>
                                                </Box>
                                            )}
                                        
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    mt: 2, 
                                                    color: theme.name === 'dark' ? theme.palette.text.secondary : theme.palette.text.primary
                                                }}
                                            >
                                                <strong>Most Common:</strong> {mostCommonModem.name !== 'None' 
                                                    ? formatModemName(mostCommonModem.name)
                                                    : 'None'} 
                                                {mostCommonModem.count > 0 && `(${Math.round((mostCommonModem.count / totalDevices) * 100)}%)`}
                                            </Typography>
                                        </>
                                    );
                                })()}
                            </Box>
                        </Box>
                    </Grid>
                            
                    {/* Right column: Label Distribution and Operator Distribution */}
                    <Grid item xs={12} md={6}>
                        {/* Label Distribution */}
                        <Box>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mb: 0.5 
                            }}>
                                <Typography variant="caption" color="text.secondary">
                                    Label Distribution
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: theme.name === 'dark' 
                                            ? theme.palette.text.secondary 
                                            : theme.palette.text.primary
                                    }}
                                >
                                    {labeledDevices} devices ({Math.round((labeledDevices / totalDevices) * 100)}%)
                                </Typography>
                            </Box>
                            <Box 
                                sx={{ 
                                    mb: 2,
                                    p: 1.5,
                                    bgcolor: theme.name === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.default, 0.5),
                                    borderRadius: 1,
                                    boxShadow: theme.name === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none',
                                }}
                            >
                                {(() => {
                                    // Prepare label data
                                    const labelCounts: Record<string, number> = {};
                                    let devicesWithLabels = 0;
                                    
                                    // Create a map to store label colors
                                    const labelColors: Record<string, string> = {};
                                    
                                    processedDevices.forEach(device => {
                                        if (device.label && Array.isArray(device.label) && device.label.length > 0) {
                                            devicesWithLabels++;
                                            device.label.forEach(labelObj => {
                                                if (labelObj && labelObj.label) {
                                                    const labelText = labelObj.label.trim();
                                                    labelCounts[labelText] = (labelCounts[labelText] || 0) + 1;
                                                    // Store the color for this label if it has one
                                                    if (labelObj.color && !labelColors[labelText]) {
                                                        labelColors[labelText] = labelObj.color;
                                                    }
                                                }
                                            });
                                        }
                                    });
                                    
                                    // Sort by count (descending)
                                    const sortedLabels = Object.entries(labelCounts)
                                        .sort(([, countA], [, countB]) => countB - countA);
                                    
                                    // Calculate percentage of devices with labels
                                    const labelPercentage = (devicesWithLabels / totalDevices) * 100;
                                    
                                    const visibleLabels = sortedLabels.slice(0, 5);
                                    const hiddenLabels = sortedLabels.slice(5);
                                    
                                    return (
                                        <>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                                                {visibleLabels.map(([label, count]) => (
                                                    <Tooltip 
                                                        key={label} 
                                                        title={`${label}: ${count} devices (${Math.round((count / totalDevices) * 100)}%)`}
                                                        arrow
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: '12px',
                                                                backgroundColor: labelColors[label]
                                                                    ? alpha(labelColors[label], theme.name === 'dark' ? 0.2 : 0.1)
                                                                    : theme.name === 'dark' 
                                                                        ? alpha(theme.palette.primary.main, 0.15)
                                                                        : alpha(theme.palette.primary.main, 0.08),
                                                                border: `1px solid ${
                                                                    labelColors[label]
                                                                        ? alpha(labelColors[label], 0.3)
                                                                        : alpha(theme.palette.primary.main, 0.2)
                                                                }`,
                                                            }}
                                                        >
                                                            <Typography 
                                                                variant="caption" 
                                                                sx={{ 
                                                                    color: labelColors[label]
                                                                        ? labelColors[label]
                                                                        : theme.name === 'dark' 
                                                                            ? theme.palette.primary.light 
                                                                            : theme.palette.primary.main,
                                                                    fontWeight: 'medium',
                                                                    mr: 0.5
                                                                }}
                                                            >
                                                                {label}
                                                            </Typography>
                                                            <Typography 
                                                                variant="caption" 
                                                                sx={{ 
                                                                    color: labelColors[label]
                                                                        ? alpha(labelColors[label], 0.8)
                                                                        : theme.name === 'dark' 
                                                                            ? alpha(theme.palette.primary.light, 0.7)
                                                                            : alpha(theme.palette.primary.main, 0.7),
                                                                }}
                                                            >
                                                                {count}
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                ))}
                                                {hiddenLabels.length > 0 && (
                                                    <Tooltip 
                                                        title={
                                                            <Box sx={{ p: 0.5 }}>
                                                                {hiddenLabels.map(([label, count]) => (
                                                                    <Typography 
                                                                        key={label} 
                                                                        variant="caption" 
                                                                        sx={{ 
                                                                            display: 'block',
                                                                            color: labelColors[label] ? labelColors[label] : 'inherit' 
                                                                        }}
                                                                    >
                                                                        {label}: {count} devices ({Math.round((count / totalDevices) * 100)}%)
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        }
                                                        arrow
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: '12px',
                                                                backgroundColor: theme.name === 'dark' 
                                                                    ? alpha(theme.palette.text.secondary, 0.1)
                                                                    : alpha(theme.palette.text.secondary, 0.05),
                                                                border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
                                                                cursor: 'help'
                                                            }}
                                                        >
                                                            <Typography 
                                                                variant="caption" 
                                                                sx={{ color: 'text.secondary' }}
                                                            >
                                                                +{hiddenLabels.length} more
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </>
                                    );
                                })()}
                            </Box>
                        </Box>

                        {/* Operator Distribution */}
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Operator Distribution
                            </Typography>
                            <Box 
                                sx={{ 
                                    mb: 2,
                                    p: 1.5,
                                    bgcolor: theme.name === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.default, 0.5),
                                    borderRadius: 1,
                                    boxShadow: theme.name === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none',
                                }}
                            >
                                {pieChartData.map((item) => (
                                    <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                width: 70,
                                                color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit'
                                            }}
                                        >
                                            {item.name}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1, mr: 1 }}>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={(item.value / totalDevices) * 100} 
                                                sx={{ 
                                                    height: 8, 
                                                    backgroundColor: theme.name === 'dark' 
                                                        ? alpha(item.color, 0.3)
                                                        : alpha(item.color, 0.2),
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: item.color
                                                    },
                                                    borderRadius: 4
                                                }}
                                            />
                                        </Box>
                                        <Typography 
                                            variant="caption"
                                            sx={{ 
                                                color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit',
                                                fontWeight: 'medium'
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        mt: 2, 
                                        color: theme.name === 'dark' ? theme.palette.text.secondary : theme.palette.text.primary
                                    }}
                                >
                                    <strong>Most Common:</strong> {mostCommonOperator.name} 
                                    ({Math.round((mostCommonOperator.value / totalDevices) * 100)}%)
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            
            {/* Additional Stats - Expandable */}
            {expanded && (
                <>
                    <Divider sx={{ borderColor: dividerColor }} />
                    <Box sx={{ p: 2, bgcolor: theme.name === 'dark' ? alpha(theme.palette.background.secondary, 0.7) : 'transparent' }}>
                        <Grid container spacing={3}>
                            {/* Middle Section: Device Status Summary */}
                            <Grid item xs={12} md={4}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                        mb: 1,
                                        color: theme.name === 'dark' ? theme.palette.text.primary : 'inherit',
                                        fontWeight: theme.name === 'dark' ? 'medium' : 'normal'
                                    }}
                                >
                                    Recent Activity
                                </Typography>
                                
                                <Box sx={{ 
                                    p: 1.5, 
                                    bgcolor: theme.name === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.default, 0.5),
                                    borderRadius: 1
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography 
                                            variant="caption"
                                            sx={{ color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit' }}
                                        >
                                            Latest Update:
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            fontWeight="medium"
                                            sx={{ color: theme.name === 'dark' ? theme.palette.text.primary : 'inherit' }}
                                        >
                                            {processedDevices
                                                .filter(d => d.time_stamp)
                                                .sort((a, b) => 
                                                    new Date(b.time_stamp || '').getTime() - 
                                                    new Date(a.time_stamp || '').getTime()
                                                )[0]?.time_stamp 
                                                ? new Date(processedDevices
                                                    .filter(d => d.time_stamp)
                                                    .sort((a, b) => 
                                                        new Date(b.time_stamp || '').getTime() - 
                                                        new Date(a.time_stamp || '').getTime()
                                                    )[0].time_stamp as string).toLocaleString() 
                                                : 'None'
                                            }
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography 
                                            variant="caption"
                                            sx={{ color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit' }}
                                        >
                                            Updates Today:
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            fontWeight="medium"
                                            sx={{ color: theme.name === 'dark' ? theme.palette.text.primary : 'inherit' }}
                                        >
                                            {processedDevices.filter(d => {
                                                if (!d.time_stamp) return false;
                                                const today = new Date();
                                                const timestamp = new Date(d.time_stamp);
                                                return timestamp.getDate() === today.getDate() &&
                                                    timestamp.getMonth() === today.getMonth() &&
                                                    timestamp.getFullYear() === today.getFullYear();
                                            }).length}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Right Section: Label Groups */}
                            <Grid item xs={12} md={4}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                        mb: 1,
                                        color: theme.name === 'dark' ? theme.palette.text.primary : 'inherit',
                                        fontWeight: theme.name === 'dark' ? 'medium' : 'normal'
                                    }}
                                >
                                    Label Distribution
                                </Typography>
                                
                                <Box sx={{ 
                                    p: 1.5, 
                                    bgcolor: theme.name === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.default, 0.5),
                                    borderRadius: 1
                                }}>
                                    {/* Label summary */}
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography 
                                                variant="caption"
                                                sx={{ color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit' }}
                                            >
                                                Labeled Devices:
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                fontWeight="medium"
                                                sx={{ color: theme.name === 'dark' ? theme.palette.text.primary : 'inherit' }}
                                            >
                                                {labeledDevices} ({Math.round((labeledDevices / totalDevices) * 100)}%)
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography 
                                                variant="caption"
                                                sx={{ color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit' }}
                                            >
                                                Most Common Label:
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                fontWeight="medium"
                                                sx={{ color: theme.name === 'dark' ? theme.palette.text.primary : 'inherit' }}
                                            >
                                                {mostCommonLabel.name !== 'None' 
                                                    ? `${mostCommonLabel.name} (${mostCommonLabel.count})` 
                                                    : 'None'
                                                }
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    {sortedLabels.length > 0 && (
                                        <Box>
                                            <Typography 
                                                variant="subtitle2" 
                                                sx={{ 
                                                    mt: 2, 
                                                    mb: 1,
                                                    color: theme.name === 'dark' ? theme.palette.text.primary : 'inherit'
                                                }}
                                            >
                                                Top Labels
                                            </Typography>
                                            {sortedLabels.slice(0, 5).map(([label, count]) => (
                                                <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            width: 80, 
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit'
                                                        }}
                                                        title={label}
                                                    >
                                                        {label}
                                                    </Typography>
                                                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                                                        <LinearProgress 
                                                            variant="determinate" 
                                                            value={(count / totalDevices) * 100} 
                                                            sx={{ 
                                                                height: 6, 
                                                                backgroundColor: theme.name === 'dark' 
                                                                    ? alpha(theme.palette.primary.main, 0.2)
                                                                    : alpha(theme.palette.primary.main, 0.1),
                                                                '& .MuiLinearProgress-bar': {
                                                                    backgroundColor: theme.name === 'dark' 
                                                                        ? theme.palette.primary.light
                                                                        : theme.palette.primary.main
                                                                },
                                                                borderRadius: 3
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography 
                                                        variant="caption"
                                                        sx={{ 
                                                            color: theme.name === 'dark' ? theme.palette.text.secondary : 'inherit',
                                                            fontWeight: 'medium'
                                                        }}
                                                    >
                                                        {count}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </>
            )}
        </Paper>
    );
});

export default DeviceDashboard; 